import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Points } from 'components/Points/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { AssetUrls } from 'config/assets';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import { SUPPORTED_LANGUAGES, getLanguageDisplayName, BULK_TIER_POINTS_THRESHOLD } from 'constants/languages';
import './StagedCardItem.css';

interface StagedCardItemProps {
  card: SidebarTradeCard;
  onUpdate: (id: string, updates: Partial<SidebarTradeCard>) => void;
  onRemove: (id: string) => void;
  onCalculatingChange?: (id: string, isCalculating: boolean) => void;
}

const CONDITION_OPTIONS = [
  { value: 'NEAR_MINT', label: 'NM' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP' },
  { value: 'MODERATELY_PLAYED', label: 'MP' },
];

const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(l => ({
  value: l.code.toLowerCase(),
  label: l.abbreviation,
}));

export const StagedCardItem: React.FC<StagedCardItemProps> = ({ card, onUpdate, onRemove, onCalculatingChange }) => {
  const [calculatePrice] = useCalculatePriceMutation();
  const [isCalculating, setIsCalculating] = useState(false);
  const hasInitialFetch = useRef(false);

  // Always recalculate price on mount to ensure displayed price matches card parameters
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchNewPrice({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSmallImageUrl = (url: string): string => {
    return url?.replace('/large/', '/small/')?.replace('/normal/', '/small/') || url;
  };

  const fetchNewPrice = async (updates: Partial<SidebarTradeCard>) => {
    const updatedCard = { ...card, ...updates };
    try {
      setIsCalculating(true);
      onCalculatingChange?.(card.id, true);
      const result = await calculatePrice({
        cards: [{
          wantListId: updatedCard.wantListId,
          cardId: updatedCard.cardId?.toString(),
          condition: updatedCard.condition,
          language: updatedCard.language,
          foil: updatedCard.foil,
          set: updatedCard.set,
        }]
      }).unwrap();

      const cardResult = result.cards[0];
      const newPrice = cardResult?.displayPrice;
      if (newPrice !== undefined) {
        onUpdate(card.id, {
          ...updates,
          pointValue: newPrice,
          languageMultiplierApplied: cardResult?.languageMultiplierApplied ?? false,
          multiplierValue: cardResult?.multiplierValue ?? 1.0,
        });
      } else {
        onUpdate(card.id, updates);
      }
    } catch (error) {
      console.error('Failed to calculate price:', error);
      onUpdate(card.id, updates);
    } finally {
      setIsCalculating(false);
      onCalculatingChange?.(card.id, false);
    }
  };

  const handleSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    // Find first matching printing for this set+collectorNumber combo
    const selectedSetDetail = card.acceptableSetDetails?.find(s => {
      const optionValue = `${s.code}-${s.collectorNumber || ''}`;
      return optionValue === selectedValue;
    });

    if (selectedSetDetail) {
      const newAvailableInFoil = selectedSetDetail.availableInFoil ?? card.availableInFoil;
      const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? card.availableInNonFoil;

      const updates: Partial<SidebarTradeCard> = {
        setCode: selectedSetDetail.code,
        set: selectedSetDetail.name,
        availableInFoil: newAvailableInFoil,
        availableInNonFoil: newAvailableInNonFoil,
      };

      if (selectedSetDetail.cardId) updates.cardId = selectedSetDetail.cardId;
      if (selectedSetDetail.imageUrl) updates.imageUrl = selectedSetDetail.imageUrl;

      const userAcceptsFoil = card.acceptFoil ?? true;
      const userAcceptsNonFoil = card.acceptNonFoil ?? true;
      const canOfferFoil = newAvailableInFoil && userAcceptsFoil;
      const canOfferNonFoil = newAvailableInNonFoil && userAcceptsNonFoil;

      if (card.foil && !canOfferFoil && canOfferNonFoil) updates.foil = false;
      else if (!card.foil && !canOfferNonFoil && canOfferFoil) updates.foil = true;

      fetchNewPrice(updates);
    }
  };

  const handleFoilChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const requestedFoil = e.target.value === 'foil';
    fetchNewPrice({ foil: requestedFoil });
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    fetchNewPrice({ condition: e.target.value });
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate(card.id, { language: e.target.value });
  };

  const physicallyAvailableInFoil = card.availableInFoil ?? false;
  const physicallyAvailableInNonFoil = card.availableInNonFoil ?? true;
  const userAcceptsFoil = card.acceptFoil ?? true;
  const userAcceptsNonFoil = card.acceptNonFoil ?? true;
  const canOfferFoil = physicallyAvailableInFoil && userAcceptsFoil;
  const canOfferNonFoil = physicallyAvailableInNonFoil && userAcceptsNonFoil;
  const showFoilDropdown = canOfferFoil && canOfferNonFoil;

  const acceptableConditions = card.acceptableConditions || CONDITION_OPTIONS.map(c => c.value);
  const availableConditions = CONDITION_OPTIONS.filter(c => acceptableConditions.includes(c.value));

  // Deduplicate acceptableSetDetails by setCode + collectorNumber so each
  // set appears once in the dropdown even when multiple language printings exist
  const dedupedSetDetails = useMemo(() => {
    if (!card.acceptableSetDetails) return [];
    const seen = new Map<string, typeof card.acceptableSetDetails[0]>();
    for (const sd of card.acceptableSetDetails) {
      const key = `${sd.code}-${sd.collectorNumber || ''}`;
      if (!seen.has(key)) {
        seen.set(key, sd);
      }
    }
    return Array.from(seen.values());
  }, [card]);

  const hasMultipleSets = dedupedSetDetails.length > 1;
  // Find the collector number for the current set from acceptableSetDetails
  const currentCollectorNumber = card.acceptableSetDetails?.find(s => s.code === card.setCode)?.collectorNumber || '';
  const currentSetValue = `${card.setCode}-${currentCollectorNumber}`;

  return (
    <div className="staged-card-item">
      <div className="staged-card-image">
        <FoilCardImage
          src={getSmallImageUrl(card.imageUrl)}
          alt={card.cardName}
          foil={card.foil}
          borderRadius="4px"
          style={{ width: '50px', height: '70px', objectFit: 'cover' }}
        />
      </div>

      <div className="staged-card-details">
        <div className="staged-card-name" title={card.cardName}>
          {card.cardName}
        </div>

        <div className="staged-card-dropdowns">
          {hasMultipleSets ? (
            <select
              value={currentSetValue}
              onChange={handleSetChange}
              className="staged-card-select set-select"
              disabled={isCalculating}
            >
              {[...dedupedSetDetails]
                .sort((a, b) => {
                  const nameCompare = a.name.localeCompare(b.name);
                  if (nameCompare !== 0) return nameCompare;
                  return parseInt(a.collectorNumber || '0', 10) - parseInt(b.collectorNumber || '0', 10);
                })
                .map(setDetail => {
                  const uniqueValue = `${setDetail.code}-${setDetail.collectorNumber || ''}`;
                  const displayText = setDetail.collectorNumber
                    ? `${setDetail.name} #${setDetail.collectorNumber}`
                    : setDetail.name;
                  return <option key={uniqueValue} value={uniqueValue}>{displayText}</option>;
                })}
            </select>
          ) : (
            <span className="staged-card-set-text">{card.set}</span>
          )}

          {showFoilDropdown ? (
            <select
              value={card.foil ? 'foil' : 'non-foil'}
              onChange={handleFoilChange}
              className="staged-card-select foil-select"
              disabled={isCalculating}
            >
              <option value="non-foil">Non-Foil</option>
              <option value="foil">Foil</option>
            </select>
          ) : (
            <span className="staged-card-static-text">
              {canOfferFoil ? 'Foil' : 'Non-Foil'}
            </span>
          )}

          {availableConditions.length > 1 ? (
            <select
              value={card.condition}
              onChange={handleConditionChange}
              className="staged-card-select condition-select"
              disabled={isCalculating}
            >
              {availableConditions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="staged-card-static-text">
              {availableConditions[0]?.label || 'NM'}
            </span>
          )}

          <select
            value={card.language}
            onChange={handleLanguageChange}
            className="staged-card-select language-select"
          >
            {LANGUAGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="staged-card-price">
          {isCalculating ? (
            <span className="staged-card-price-loading">...</span>
          ) : (
            <Points points={card.pointValue} type="small" />
          )}
          {!isCalculating && card.languageMultiplierApplied && card.pointValue >= BULK_TIER_POINTS_THRESHOLD && (
            <span className="staged-card-price-annotation" title="Non-English card values are estimated using market data and may not reflect your card's exact market price.">
              {getLanguageDisplayName(card.language)} pricing
            </span>
          )}
        </div>
      </div>

      <button
        className="staged-card-remove"
        onClick={() => onRemove(card.id)}
        aria-label="Remove card"
      >
        <img
          src={AssetUrls.icons.dropdowns.close}
          alt="Remove"
          className="staged-card-remove-icon"
        />
      </button>
    </div>
  );
};

export default StagedCardItem;
